import React, { useState, useRef } from 'react'
import { Upload, Camera, AlertCircle, GraduationCap, X } from 'lucide-react'
import MagicalLoader from './MagicalLoader'
import UltrasoundFrame from './UltrasoundFrame'
import './ImageUpload.css'

interface EnhancedImageUploadProps {
  onImageUpload: (file: File, knownGender?: string, isTrainingMode?: boolean) => void
  isAnalyzing: boolean
}

const EnhancedImageUpload: React.FC<EnhancedImageUploadProps> = ({ 
  onImageUpload, 
  isAnalyzing 
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isTrainingMode, setIsTrainingMode] = useState(false)
  const [knownGender, setKnownGender] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = (file: File) => {
    setError(null)

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // If in training mode with known gender, pass that info
    if (isTrainingMode && knownGender) {
      onImageUpload(file, knownGender, true)
    } else {
      onImageUpload(file)
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const resetUpload = () => {
    setPreviewUrl(null)
    setKnownGender(null)
    setIsTrainingMode(false)
  }

  return (
    <div className="image-upload-container">
      <div className="upload-mode-toggle">
        <button
          className={`mode-button ${!isTrainingMode ? 'active' : ''}`}
          onClick={() => {
            setIsTrainingMode(false)
            setKnownGender(null)
          }}
        >
          <Camera size={16} />
          Prediction Mode
        </button>
        <button
          className={`mode-button ${isTrainingMode ? 'active' : ''}`}
          onClick={() => setIsTrainingMode(true)}
        >
          <GraduationCap size={16} />
          Training Mode (I know the gender)
        </button>
      </div>

      {isTrainingMode && !previewUrl && (
        <div className="training-mode-options">
          <p>Select the actual gender of the baby:</p>
          <div className="gender-buttons">
            <button
              className={`gender-button boy ${knownGender === 'boy' ? 'selected' : ''}`}
              onClick={() => setKnownGender('boy')}
            >
              👶 Boy
            </button>
            <button
              className={`gender-button girl ${knownGender === 'girl' ? 'selected' : ''}`}
              onClick={() => setKnownGender('girl')}
            >
              👶 Girl
            </button>
          </div>
          {!knownGender && (
            <p className="hint">Please select the gender before uploading</p>
          )}
        </div>
      )}

      {(!isTrainingMode || knownGender) && (
        <>
          {!previewUrl ? (
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''} ${isAnalyzing ? 'analyzing' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleChange}
                className="file-input"
                disabled={isAnalyzing || (isTrainingMode && !knownGender)}
              />
              
              {isAnalyzing ? (
                <MagicalLoader />
              ) : (
                <div className="upload-content">
                  <Upload className="upload-icon" size={48} />
                  <h3>Upload Ultrasound Image</h3>
                  <p>Drag & drop or click to select</p>
                  <p className="upload-hint">12-15 week scan recommended</p>
                  <button onClick={handleButtonClick} className="upload-button">
                    Choose File
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="preview-container">
              <button className="close-preview" onClick={resetUpload}>
                <X size={20} />
              </button>
              <UltrasoundFrame size={400}>
                {isAnalyzing ? (
                  <MagicalLoader />
                ) : (
                  <img 
                    src={previewUrl} 
                    alt="Ultrasound preview" 
                    className="preview-image"
                  />
                )}
              </UltrasoundFrame>
              {isTrainingMode && knownGender && (
                <div className="training-info">
                  <p>Training Mode: Known Gender - <strong>{knownGender}</strong></p>
                  <p className="hint">The AI will learn from this example</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  )
}

export default EnhancedImageUpload